import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Home, ChevronLeft, ChevronRight, Check, Lock, Upload, X, Star,
  ShieldCheck, AlertTriangle, MapPin, Search as SearchIcon, Building2, ListChecks,
  FileText, Rocket, Copy, ExternalLink, Download, Sparkles,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toPng } from "html-to-image";
import { useAuth, login } from "@/context/AuthContext";
import { homesCreateListing, homesUploadPhoto, homesUploadDocument, homesMarketingKit, homesListing, homesUpdateListing, homesAiDescription, search as geoSearch } from "@/lib/api";
import {
  PURPOSES, CATEGORIES, SUBTYPES, LISTING_STATUSES, LISTING_SOURCES, OCCUPANCY, POSSESSION,
  CONDITIONS, OWNERSHIP_TYPES, LIVING_AREA_SOURCES, FINANCING, SALE_CONDITIONS, HOA_FREQ,
  FURNISHED, LEASE_TYPES, LEASE_RATE_UNITS, ADDRESS_PRIVACY, CONTACT_TYPES,
  INTERIOR_FEATURES, ACCESSIBILITY_FEATURES, EXTERIOR_FEATURES, COMMUNITY_FEATURES,
  WATER_SOURCES, SEWER, SOLAR_OWNERSHIP, scanFairHousing, toPropertyType, toStatus,
} from "./listing/listingOptions";
import { SectionTitle, Label, Text, Num, Select, TextArea, CheckGroup, Radio, Toggle, Grid } from "./listing/fields";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CONFIRMATIONS = [
  { k: "authorized", label: "I am authorized to advertise this property." },
  { k: "accurate", label: "The information is accurate to the best of my knowledge." },
  { k: "media_rights", label: "I own or have permission to publish all uploaded media." },
  { k: "fair_housing", label: "I will comply with Fair Housing laws." },
  { k: "terms", label: "I accept the HomesPulse Listing Terms." },
  { k: "authorize_display", label: "I authorize HomesPulse to display this listing." },
  { k: "moderation", label: "I understand HomesPulse may verify, reject or remove the listing." },
  { k: "update_status", label: "I will update the property's status promptly." },
  { k: "consent_contact", label: "I consent to the selected contact information being displayed." },
];

const EMPTY = {
  purpose: "", category: "residential", subtype: "", listing_status: "active", listing_source: "owner",
  mls_number: "", ref_number: "",
  street_number: "", street_dir: "", street_name: "", street_suffix: "", unit: "",
  city_geo_id: "", city_label: "", zip: "", county: "", subdivision: "", school_district: "",
  address_privacy: "exact",
  headline: "", description: "", highlights: "", showing_instructions: "", occupancy: "", possession: "",
  price: "", rent: "", beds: "", full_baths: "", half_baths: "", sqft: "", lot_size: "",
  year_built: "", stories: "", garage: "", condition: "", ownership: "", living_area_source: "",
  interior: [], accessibility: [], exterior: [], community: [],
  photos: [],
  contact_type: "owner", contact_first: "", contact_last: "", contact_phone: "", contact_email: "",
  contact_website: "", brokerage: "", license: "",
  confirmations: {}, signature: "",
  details: {},
};

// Reverse-map an existing listing document back into the wizard's form shape (edit mode).
function hydrateForm(l) {
  const det = l.details || {};
  const st = det.street || {};
  const prov = l.provenance || {};
  const contact = prov.contact || det.contact || {};
  const nameParts = (contact.name || "").trim().split(/\s+/);
  const rent = l.status === "for_rent";
  return {
    ...EMPTY,
    purpose: l.purpose || (rent ? "for_rent" : "for_sale"),
    category: l.category || "residential",
    subtype: l.subtype || "",
    listing_status: l.listing_status || "active",
    listing_source: l.source || prov.source || "owner",
    mls_number: det.mls_number || "", ref_number: det.ref_number || "",
    street_number: st.number || "", street_dir: st.dir || "", street_name: st.name || (l.address || ""),
    street_suffix: st.suffix || "", unit: st.unit || "",
    city_geo_id: l.city_geo_id || "", city_label: l.city_name || "",
    zip: det.zip || "", county: det.county || "", subdivision: det.subdivision || "",
    school_district: det.school_district || "", address_privacy: l.address_privacy || "exact",
    headline: l.headline || "", description: l.description || "", highlights: det.highlights || "",
    showing_instructions: det.showing_instructions || "", occupancy: det.occupancy || "", possession: det.possession || "",
    price: rent ? "" : (l.price || ""), rent: rent ? (l.price || "") : "",
    beds: l.beds || "", full_baths: det.full_baths || "", half_baths: det.half_baths || "",
    sqft: l.sqft || "", lot_size: det.lot_size || "", year_built: l.year_built || "",
    stories: det.stories || "", garage: det.garage_spaces || "", condition: det.condition || "",
    ownership: det.ownership || "", living_area_source: det.living_area_source || "",
    interior: det.interior || [], exterior: det.exterior || [], accessibility: det.accessibility || [], community: det.community || [],
    photos: l.photos || [],
    contact_type: prov.contact_type || (contact.type) || "owner",
    contact_first: nameParts[0] || "", contact_last: nameParts.slice(1).join(" ") || "",
    contact_phone: contact.phone || "", contact_email: contact.email || "",
    contact_website: (det.contact || {}).website || "", brokerage: (det.contact || {}).brokerage || "",
    license: (det.contact || {}).license || "",
    confirmations: prov.confirmations || {}, signature: prov.signature || "",
    details: det,
  };
}

export default function ListProperty() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [boostListing, setBoostListing] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(!!editId);

  // Edit mode: hydrate the wizard from the existing listing so nothing is re-entered.
  useEffect(() => {
    if (!editId || !user) return;
    setLoadingEdit(true);
    homesListing(editId).then((d) => {
      setForm(hydrateForm(d.listing));
    }).catch(() => toast.error("Could not load that listing to edit.")).finally(() => setLoadingEdit(false));
  }, [editId, user]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const d = (k) => (v) => setForm((f) => ({ ...f, details: { ...f.details, [k]: v } }));
  const dv = (k) => form.details[k];
  const toggleArr = (k) => (opt) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(opt) ? f[k].filter((x) => x !== opt) : [...f[k], opt] }));

  const isRental = ["for_rent", "for_lease", "vacation_rental"].includes(form.purpose);
  const isLand = form.category === "land" || form.category === "farm_ranch";
  const isCommercial = form.category === "commercial";
  const isMulti = form.category === "residential_income";
  const subtypeOptions = useMemo(
    () => SUBTYPES.filter((s) => s.cats.includes(form.category)), [form.category]);

  const fhFlags = useMemo(
    () => scanFairHousing(`${form.headline} ${form.description} ${form.highlights}`), [form.headline, form.description, form.highlights]);

  // Build ordered step list (category-specific step inserted conditionally)
  const steps = useMemo(() => {
    const base = [
      { key: "purpose", label: "Purpose & Type", icon: Home },
      { key: "location", label: "Location", icon: MapPin },
      { key: "price", label: "Price & Financial", icon: Building2 },
      { key: "facts", label: "Property Facts", icon: ListChecks },
      { key: "features", label: "Features", icon: Check },
      { key: "description", label: "Description", icon: ListChecks },
      { key: "utilities", label: "Utilities & Environment", icon: ShieldCheck },
      { key: "schools", label: "Schools & Neighborhood", icon: MapPin },
    ];
    if (isLand || isCommercial || isMulti) base.push({ key: "category", label: "Category Details", icon: Building2 });
    base.push({ key: "photos", label: "Photos & Media", icon: Upload });
    base.push({ key: "openhouse", label: "Open House", icon: MapPin });
    base.push({ key: "contact", label: "Contact", icon: Home });
    base.push({ key: "verify", label: "Verify & Submit", icon: ShieldCheck });
    return base;
  }, [isLand, isCommercial, isMulti]);

  useEffect(() => { if (step >= steps.length) setStep(steps.length - 1); }, [steps.length, step]);
  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const current = steps[step]?.key;

  const validate = () => {
    if (current === "purpose" && (!form.purpose || !form.category || !form.subtype))
      return "Please choose a purpose, category and subtype.";
    if (current === "location" && (!form.street_name || !form.city_geo_id || !form.zip))
      return "Street name, city and ZIP code are required.";
    if (current === "description" && (!form.headline || !form.description))
      return "A headline and public description are required.";
    if (current === "price" && !isRental && !form.price)
      return "List price is required.";
    if (current === "price" && isRental && !form.rent)
      return "Monthly rent is required.";
    if (current === "contact" && (!form.contact_first || !form.contact_last || !form.contact_phone || !form.contact_email))
      return "Contact first/last name, phone and email are required.";
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  // Full required-field validation across ALL steps (used on final Publish).
  const fullValidate = () => {
    const checks = [
      { ok: form.purpose && form.category && form.subtype, key: "purpose", msg: "Choose a purpose, category and subtype." },
      { ok: form.street_name && form.city_geo_id && form.zip, key: "location", msg: "Street name, city and ZIP code are required." },
      { ok: form.headline && form.description, key: "description", msg: "A headline and public description are required." },
      { ok: isRental ? !!form.rent : !!form.price, key: "price", msg: isRental ? "Monthly rent is required." : "List price is required." },
      { ok: form.contact_first && form.contact_last && form.contact_phone && form.contact_email, key: "contact", msg: "Contact first/last name, phone and email are required." },
    ];
    const bad = checks.find((c) => !c.ok);
    if (!bad) return null;
    const idx = steps.findIndex((s) => s.key === bad.key);
    if (idx >= 0) setStep(idx);
    return bad.msg;
  };

  const buildPayload = (asDraft) => {
    const address = [form.street_number, form.street_dir, form.street_name, form.street_suffix,
      form.unit ? `#${form.unit}` : ""].filter(Boolean).join(" ").trim();
    const features = [...form.interior, ...form.exterior, ...form.accessibility, ...form.community];
    const price = Number(isRental ? form.rent : form.price) || 0;
    const total_baths = (Number(form.full_baths) || 0) + (Number(form.half_baths) || 0) * 0.5;
    return {
      status: toStatus(form.purpose),
      property_type: toPropertyType(form.category, form.subtype),
      price,
      beds: Number(form.beds) || 0,
      baths: total_baths,
      sqft: form.sqft ? Number(form.sqft) : null,
      year_built: form.year_built ? Number(form.year_built) : null,
      address,
      city_geo_id: form.city_geo_id,
      headline: form.headline,
      description: form.description,
      features,
      photos: form.photos,
      purpose: form.purpose,
      category: form.category,
      subtype: form.subtype,
      listing_status: asDraft ? "draft" : (form.listing_status || "active"),
      listing_source: form.listing_source,
      save_as_draft: asDraft,
      address_privacy: form.address_privacy,
      contact_type: form.contact_type,
      contact_name: `${form.contact_first} ${form.contact_last}`.trim(),
      contact_email: form.contact_email,
      contact_phone: form.contact_phone,
      confirmations: form.confirmations,
      signature: form.signature,
      details: {
        ...form.details,
        mls_number: form.mls_number, ref_number: form.ref_number,
        street: { number: form.street_number, dir: form.street_dir, name: form.street_name, suffix: form.street_suffix, unit: form.unit },
        zip: form.zip, county: form.county, subdivision: form.subdivision, school_district: form.school_district,
        full_baths: Number(form.full_baths) || 0, half_baths: Number(form.half_baths) || 0,
        lot_size: form.lot_size, stories: form.stories, garage_spaces: form.garage,
        condition: form.condition, ownership: form.ownership, living_area_source: form.living_area_source,
        highlights: form.highlights, showing_instructions: form.showing_instructions,
        occupancy: form.occupancy, possession: form.possession,
        interior: form.interior, exterior: form.exterior, accessibility: form.accessibility, community: form.community,
        contact: {
          type: form.contact_type, website: form.contact_website,
          brokerage: form.brokerage, license: form.license,
        },
      },
    };
  };

  const submit = async (asDraft) => {
    if (!asDraft) {
      const fv = fullValidate();
      if (fv) { toast.error(fv); return; }
      const c = form.confirmations;
      const missing = CONFIRMATIONS.filter((x) => !c[x.k]);
      if (missing.length) { toast.error("Please confirm all listing acknowledgements."); return; }
      if (!form.signature.trim()) { toast.error("Please add your electronic signature."); return; }
    }
    if (!form.purpose || !form.category || !form.subtype) { toast.error("Complete the Purpose & Type step first."); return; }
    if (!form.city_geo_id) { toast.error("Please select the property's city/town."); return; }
    setSubmitting(true);
    try {
      if (editId) {
        const res = await homesUpdateListing(editId, buildPayload(asDraft));
        toast.success(asDraft ? "Draft saved" : "Listing updated");
        nav(asDraft ? "/homes/agent-portal" : `/homes/property/${res.id}`);
      } else {
        const res = await homesCreateListing(buildPayload(asDraft));
        if (asDraft) {
          toast.success("Draft saved");
          nav("/homes/agent-portal");
        } else {
          toast.success("Listing published!");
          setBoostListing(res);   // show the Boost modal (QR + flyer) instead of leaving immediately
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the listing. Please review your entries.");
    } finally { setSubmitting(false); }
  };

  if (authLoading || loadingEdit) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;
  if (!user) return (
    <Center>
      <Lock className="h-10 w-10 text-slate-300" />
      <h1 className="mt-4 text-2xl font-black text-[#0F172A]">Sign in to list your property</h1>
      <p className="mt-1 text-slate-500">Owners and agents can post homes for sale or rent — free.</p>
      <button onClick={() => login("/homes/list-property")} className="mt-5 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white hover:bg-[#1D4ED8]" data-testid="list-signin">Sign in</button>
    </Center>
  );

  return (
    <div data-testid="list-property">
      <Hero editing={!!editId} />
      <div className="mx-auto max-w-[1100px] px-4 py-8">
        <Stepper steps={steps} step={step} onJump={setStep} />

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          {current === "purpose" && <StepPurpose {...{ form, set, d, dv, subtypeOptions }} />}
          {current === "location" && <StepLocation {...{ form, set }} />}
          {current === "description" && <StepDescription {...{ form, set, fhFlags }} />}
          {current === "price" && <StepPrice {...{ form, set, d, dv, isRental, isCommercial }} />}
          {current === "facts" && <StepFacts {...{ form, set, isLand }} />}
          {current === "features" && <StepFeatures {...{ form, toggleArr }} />}
          {current === "utilities" && <StepUtilities {...{ d, dv }} />}
          {current === "schools" && <StepSchools {...{ form, set, d, dv }} />}
          {current === "category" && <StepCategory {...{ d, dv, isLand, isCommercial, isMulti, form, setForm }} />}
          {current === "photos" && <StepPhotos {...{ form, setForm }} />}
          {current === "openhouse" && <StepOpenHouse {...{ d, dv }} />}
          {current === "contact" && <StepContact {...{ form, set }} />}
          {current === "verify" && <StepVerify {...{ form, set, fhFlags }} />}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button onClick={back} disabled={step === 0}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-[#0F172A] transition-colors hover:bg-slate-50 disabled:opacity-40" data-testid="list-back">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => submit(true)} disabled={submitting}
              className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-[#0F172A] transition-colors hover:bg-slate-50 disabled:opacity-50" data-testid="list-save-draft">
              Save as draft
            </button>
            {current !== "verify" ? (
              <button onClick={next}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#2563EB] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="list-next">
                Continue <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={() => submit(false)} disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#2563EB] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60" data-testid="list-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Publish listing
              </button>
            )}
          </div>
        </div>
      </div>
      {boostListing && <BoostModal listing={boostListing} onClose={() => nav(`/homes/property/${boostListing.id}`)} onMarketing={() => nav("/homes/marketing")} />}
    </div>
  );
}

/* ---------------- Hero (prominent, attention-grabbing) ---------------- */
const Hero = ({ editing }) => (
  <section className="relative overflow-hidden bg-[#0F172A] text-white">
    <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#2563EB]/30 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-[#2563EB]/20 blur-3xl" />
    <div className="relative mx-auto max-w-[1100px] px-4 py-14 text-center">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2563EB]/20 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#93B4FF]">
        <Home className="h-3.5 w-3.5" /> {editing ? "Editing your listing" : "List for free — every town welcome"}
      </span>
      <h1 className="mt-4 text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
        {editing ? <>Update your <span className="text-[#93B4FF]">HomesPulse.pro</span> listing</>
          : <>Sell or rent your property on <span className="text-[#93B4FF]">HomesPulse.pro</span></>}
      </h1>
      <p className="mx-auto mt-4 max-w-2xl text-base text-white/70 md:text-lg">
        {editing ? "Everything you entered is pre-filled — change what you need and save. Your photos and documents are kept."
          : "Owners, landlords and agents — reach buyers and renters searching every city, county and small town in America. Add photos, set your price, and go live in minutes."}
      </p>
    </div>
  </section>
);

/* ---------------- Stepper ---------------- */
const Stepper = ({ steps, step, onJump }) => (
  <div className="flex gap-2 overflow-x-auto pb-2" data-testid="list-stepper">
    {steps.map((s, i) => {
      const active = i === step, done = i < step;
      return (
        <button key={s.key} onClick={() => onJump(i)}
          className={`flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${active ? "border-[#2563EB] bg-[#2563EB] text-white" : done ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500"}`}>
          <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${active ? "bg-white/20" : done ? "bg-emerald-100" : "bg-slate-100"}`}>
            {done ? <Check className="h-3 w-3" /> : i + 1}
          </span>
          {s.label}
        </button>
      );
    })}
  </div>
);

/* ---------------- Steps ---------------- */
function StepPurpose({ form, set, d, dv, subtypeOptions }) {
  return (
    <div>
      <SectionTitle sub="Tell us what you're listing. We'll only ask for the fields that apply.">Listing purpose & type</SectionTitle>
      <Grid>
        <Select label="Listing purpose" req value={form.purpose} onChange={set("purpose")} options={PURPOSES} testid="list-purpose" />
        <Select label="Property category" req value={form.category} onChange={(v) => { set("category")(v); set("subtype")(""); }} options={CATEGORIES} testid="list-category" />
        <Select label="Property subtype" req value={form.subtype} onChange={set("subtype")} options={subtypeOptions} testid="list-subtype" />
        <Select label="Listing status" value={form.listing_status} onChange={set("listing_status")} options={LISTING_STATUSES} placeholder="Active" testid="list-status" />
        <Select label="Listing source" req value={form.listing_source} onChange={set("listing_source")} options={LISTING_SOURCES} testid="list-source" />
        <Text label="Available date" type="date" value={dv("available_date")} onChange={d("available_date")} testid="list-available-date" />
        <Text label="MLS number (optional)" value={form.mls_number} onChange={set("mls_number")} testid="list-mls" />
        <Text label="Internal reference # (optional)" value={form.ref_number} onChange={set("ref_number")} testid="list-ref" />
      </Grid>
    </div>
  );
}

function StepLocation({ form, set }) {
  return (
    <div>
      <SectionTitle sub="Enter the property address. You control how much is shown publicly.">Property address & location</SectionTitle>
      <Grid>
        <Text label="Street number" req value={form.street_number} onChange={set("street_number")} testid="list-street-number" />
        <Text label="Street direction" value={form.street_dir} onChange={set("street_dir")} placeholder="N, SW…" testid="list-street-dir" />
        <Text label="Street name" req value={form.street_name} onChange={set("street_name")} testid="list-street-name" />
        <Text label="Street suffix" value={form.street_suffix} onChange={set("street_suffix")} placeholder="St, Ave, Blvd" testid="list-street-suffix" />
        <Text label="Unit / suite / apt" value={form.unit} onChange={set("unit")} testid="list-unit" />
        <CitySearch value={form.city_geo_id} label={form.city_label} onSelect={(geo) => { set("city_geo_id")(geo.geo_id); set("city_label")(geo.label); }} />
        <Text label="ZIP code" req value={form.zip} onChange={set("zip")} testid="list-zip" />
        <Text label="County" value={form.county} onChange={set("county")} testid="list-county" />
        <Text label="Subdivision / development" value={form.subdivision} onChange={set("subdivision")} testid="list-subdivision" />
        <Text label="School district" value={form.school_district} onChange={set("school_district")} testid="list-school-district" />
      </Grid>
      <div className="mt-5">
        <Radio label="Address privacy" req value={form.address_privacy} onChange={set("address_privacy")} options={ADDRESS_PRIVACY} testid="list-address-privacy" />
      </div>
      <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-800">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        Sellers may direct that a property address be withheld from public display. Choose "Withhold" if you're not authorized to display the exact address online.
      </p>
    </div>
  );
}

function CitySearch({ value, label, onSelect }) {
  const [q, setQ] = useState(label || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setQ(label || ""); }, [label]);
  const onChange = (val) => {
    setQ(val); setOpen(true);
    clearTimeout(timer.current);
    if (val.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(() => {
      geoSearch(val.trim()).then((d) => {
        const cities = (d.results || []).filter((r) => ["city", "town", "cdp", "village"].includes(r.geo_type));
        setResults(cities.slice(0, 8));
      }).catch(() => setResults([]));
    }, 300);
  };
  return (
    <div className="relative">
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">City / town <span className="text-[#c8102e]">*</span></label>
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => onChange(e.target.value)} onFocus={() => setOpen(true)}
          placeholder="Search any city or town…" data-testid="list-city-search"
          className={`w-full rounded-xl border ${value ? "border-emerald-300" : "border-slate-300"} bg-white py-2.5 pl-9 pr-3 text-sm text-[#0F172A] outline-none focus:border-[#2563EB]`} />
      </div>
      {value && <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check className="h-3 w-3" /> Selected</span>}
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="list-city-results">
          {results.map((r) => (
            <button key={r.geo_id} type="button"
              onClick={() => { onSelect({ geo_id: r.geo_id, label: `${r.name}` }); setQ(r.name); setOpen(false); }}
              className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-slate-50">
              <MapPin className="h-3.5 w-3.5 text-[#2563EB]" />
              <span className="font-semibold text-[#0F172A]">{r.name}</span>
              <span className="ml-auto text-xs text-slate-400">{r.url_path?.split("/")[2]?.replace(/-/g, " ")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StepDescription({ form, set, fhFlags }) {
  const [aiBusy, setAiBusy] = useState(false);
  const generate = async () => {
    setAiBusy(true);
    try {
      const facts = {
        purpose: form.purpose, category: form.category, subtype: form.subtype, status: form.purpose,
        price: Number(form.price || form.rent) || undefined,
        beds: Number(form.beds) || undefined, baths: (Number(form.full_baths) || 0) + (Number(form.half_baths) || 0) * 0.5 || undefined,
        sqft: Number(form.sqft) || undefined, year_built: Number(form.year_built) || undefined,
        city_name: form.city_label || undefined, condition: form.condition || undefined,
        lot_size: form.lot_size || undefined, highlights: form.highlights || undefined,
        features: [...form.interior, ...form.exterior, ...form.accessibility, ...form.community],
      };
      const res = await homesAiDescription(facts);
      set("description")(res.description);
      toast[res.source === "ai" ? "success" : "message"](res.source === "ai" ? "AI description added — review and tweak" : (res.note || "Added a quick draft — edit as you like"));
    } catch {
      toast.error("Couldn't generate a description right now.");
    } finally { setAiBusy(false); }
  };
  return (
    <div>
      <SectionTitle sub="Describe the property factually. We flag language that may raise Fair Housing concerns.">Listing headline & description</SectionTitle>
      <Grid>
        <Text label="Listing headline" req span value={form.headline} onChange={set("headline")} placeholder="Charming updated ranch with large yard" testid="list-headline" />
        <div className="sm:col-span-2">
          <div className="mb-1.5 flex items-center justify-between">
            <Label req>Public property description</Label>
            <button type="button" onClick={generate} disabled={aiBusy}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF2FF] px-3 py-1.5 text-xs font-bold text-[#2563EB] transition-colors hover:bg-[#2563EB] hover:text-white disabled:opacity-60" data-testid="list-ai-generate">
              {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate with AI
            </button>
          </div>
          <textarea rows={6} value={form.description ?? ""} onChange={(e) => set("description")(e.target.value)}
            placeholder="Describe the home's features, updates and layout… or click Generate with AI."
            className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] outline-none transition-colors focus:border-[#2563EB]" data-testid="list-description" />
        </div>
        <TextArea label="Key selling points / highlights" rows={3} value={form.highlights} onChange={set("highlights")} testid="list-highlights" />
        <TextArea label="Showing instructions (private)" rows={2} value={form.showing_instructions} onChange={set("showing_instructions")} testid="list-showing" />
        <Select label="Occupancy status" value={form.occupancy} onChange={set("occupancy")} options={OCCUPANCY} span={false} testid="list-occupancy" />
        <Select label="Possession" value={form.possession} onChange={set("possession")} options={POSSESSION} span={false} testid="list-possession" />
      </Grid>
      <FairHousingNotice flags={fhFlags} />
    </div>
  );
}

const FairHousingNotice = ({ flags }) => (
  flags.length > 0 ? (
    <div className="mt-5 rounded-xl border border-[#c8102e]/30 bg-red-50 p-4" data-testid="list-fh-warning">
      <div className="flex items-center gap-2 font-bold text-[#c8102e]"><AlertTriangle className="h-4 w-4" /> Fair Housing language check</div>
      <p className="mt-1 text-sm text-red-700">Consider rephrasing — the following may imply a preference based on a protected class:</p>
      <ul className="mt-2 space-y-1 text-sm text-red-800">
        {flags.map((f) => <li key={f.term}>• <span className="font-semibold">"{f.term}"</span> — {f.why}</li>)}
      </ul>
    </div>
  ) : (
    <p className="mt-5 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 text-sm text-emerald-700" data-testid="list-fh-ok">
      <ShieldCheck className="h-4 w-4" /> No Fair Housing concerns detected. Describe the property, not the ideal occupant.
    </p>
  )
);

function StepPrice({ form, set, d, dv, isRental, isCommercial }) {
  const price = Number(form.price) || 0;
  const monthly = price ? Math.round((price * 0.8 * (0.068 / 12)) / (1 - Math.pow(1 + 0.068 / 12, -360)) + (price * 0.011) / 12) : 0;
  return (
    <div>
      <SectionTitle sub="Only the fields relevant to your listing type are shown.">Price & financial information</SectionTitle>
      {!isRental && (
        <Grid>
          <Num label="List price ($)" req value={form.price} onChange={set("price")} testid="list-price" />
          <Num label="Original list price ($)" value={dv("original_price")} onChange={d("original_price")} testid="list-original-price" />
          {form.sqft && price ? <ReadOnly label="Price / sq ft" value={`$${Math.round(price / Number(form.sqft)).toLocaleString()}`} /> : <div />}
          {monthly ? <ReadOnly label="Est. monthly payment" value={`~$${monthly.toLocaleString()}/mo`} hint="20% down · 6.8% · 30yr + est. tax" /> : <div />}
          <CheckGroupInline label="Financing accepted" options={FINANCING} value={dv("financing") || []} onChange={d("financing")} />
          <Select label="Special sale conditions" value={dv("sale_condition")} onChange={d("sale_condition")} options={SALE_CONDITIONS} span={false} testid="list-sale-condition" />
          <Num label="Seller concessions ($)" value={dv("concessions")} onChange={d("concessions")} testid="list-concessions" />
        </Grid>
      )}
      {isRental && (
        <Grid>
          <Num label="Monthly rent ($)" req value={form.rent} onChange={set("rent")} testid="list-rent" />
          <Num label="Security deposit ($)" value={dv("deposit")} onChange={d("deposit")} testid="list-deposit" />
          <Num label="Application fee ($)" value={dv("app_fee")} onChange={d("app_fee")} testid="list-app-fee" />
          <Text label="Lease duration" value={dv("lease_duration")} onChange={d("lease_duration")} placeholder="12 months" testid="list-lease-duration" />
          <Select label="Furnished?" value={dv("furnished")} onChange={d("furnished")} options={FURNISHED} span={false} testid="list-furnished" />
          <Text label="Utilities included" value={dv("utilities_included")} onChange={d("utilities_included")} testid="list-utils-inc" />
        </Grid>
      )}
      <div className="mt-6 border-t border-slate-100 pt-6">
        <h3 className="mb-4 text-sm font-black uppercase tracking-widest text-slate-500">Taxes & association costs</h3>
        <Grid>
          <Num label="Annual property tax ($)" value={dv("annual_tax")} onChange={d("annual_tax")} testid="list-annual-tax" />
          <Num label="Tax year" value={dv("tax_year")} onChange={d("tax_year")} testid="list-tax-year" />
          <Num label="HOA fee ($)" value={dv("hoa_fee")} onChange={d("hoa_fee")} testid="list-hoa-fee" />
          <Select label="HOA frequency" value={dv("hoa_freq")} onChange={d("hoa_freq")} options={HOA_FREQ} span={false} testid="list-hoa-freq" />
        </Grid>
      </div>
      {isCommercial && (
        <div className="mt-6 border-t border-slate-100 pt-6">
          <h3 className="mb-4 text-sm font-black uppercase tracking-widest text-slate-500">Commercial financials</h3>
          <Grid>
            <Select label="Lease type" value={dv("lease_type")} onChange={d("lease_type")} options={LEASE_TYPES} span={false} testid="list-lease-type" />
            <Select label="Lease rate unit" value={dv("lease_rate_unit")} onChange={d("lease_rate_unit")} options={LEASE_RATE_UNITS} span={false} testid="list-lease-rate-unit" />
            <Num label="Net operating income ($)" value={dv("noi")} onChange={d("noi")} testid="list-noi" />
            <Num label="Cap rate (%)" step="0.1" value={dv("cap_rate")} onChange={d("cap_rate")} testid="list-cap-rate" />
          </Grid>
        </div>
      )}
    </div>
  );
}

const ReadOnly = ({ label, value, hint }) => (
  <div>
    <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-bold text-[#0F172A]">{value}</div>
    {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
  </div>
);

const CheckGroupInline = ({ label, options, value, onChange }) => (
  <div className="sm:col-span-2">
    <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = value.includes(o);
        return <button type="button" key={o} onClick={() => onChange(active ? value.filter((x) => x !== o) : [...value, o])}
          className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${active ? "border-[#2563EB] bg-[#EEF2FF] text-[#2563EB]" : "border-slate-300 bg-white text-slate-600 hover:border-[#2563EB]"}`}>{o}</button>;
      })}
    </div>
  </div>
);

function StepFacts({ form, set, isLand }) {
  return (
    <div>
      <SectionTitle sub="Core property facts used for search and comparisons.">Property facts</SectionTitle>
      <Grid>
        {!isLand && <Num label="Bedrooms" value={form.beds} onChange={set("beds")} testid="list-beds" />}
        {!isLand && <Num label="Full bathrooms" value={form.full_baths} onChange={set("full_baths")} testid="list-full-baths" />}
        {!isLand && <Num label="Half bathrooms" value={form.half_baths} onChange={set("half_baths")} testid="list-half-baths" />}
        <Num label="Living area (sq ft)" value={form.sqft} onChange={set("sqft")} testid="list-sqft" />
        <Select label="Living-area source" value={form.living_area_source} onChange={set("living_area_source")} options={LIVING_AREA_SOURCES} span={false} testid="list-area-source" />
        <Text label="Lot size" value={form.lot_size} onChange={set("lot_size")} placeholder="0.25 acres / 10,000 sq ft" testid="list-lot-size" />
        <Num label="Year built" value={form.year_built} onChange={set("year_built")} testid="list-year-built" />
        <Num label="Stories / levels" value={form.stories} onChange={set("stories")} testid="list-stories" />
        <Num label="Garage spaces" value={form.garage} onChange={set("garage")} testid="list-garage" />
        <Select label="Property condition" value={form.condition} onChange={set("condition")} options={CONDITIONS} span={false} testid="list-condition" />
        <Select label="Ownership type" value={form.ownership} onChange={set("ownership")} options={OWNERSHIP_TYPES} span={false} testid="list-ownership" />
      </Grid>
    </div>
  );
}

function StepFeatures({ form, toggleArr }) {
  return (
    <div>
      <SectionTitle sub="Select all that apply.">Interior, exterior & community features</SectionTitle>
      <div className="space-y-6">
        <CheckGroup label="Interior features" options={INTERIOR_FEATURES} values={form.interior} onToggle={toggleArr("interior")} testid="list-interior" />
        <CheckGroup label="Exterior features" options={EXTERIOR_FEATURES} values={form.exterior} onToggle={toggleArr("exterior")} testid="list-exterior" />
        <CheckGroup label="Accessibility features" options={ACCESSIBILITY_FEATURES} values={form.accessibility} onToggle={toggleArr("accessibility")} testid="list-accessibility" />
        <CheckGroup label="Community amenities" options={COMMUNITY_FEATURES} values={form.community} onToggle={toggleArr("community")} testid="list-community" />
      </div>
    </div>
  );
}

function StepUtilities({ d, dv }) {
  return (
    <div>
      <SectionTitle sub="Informational only — attributed to you as the source; HomesPulse does not guarantee hazard accuracy.">Utilities & environmental information</SectionTitle>
      <Grid>
        <Select label="Water source" value={dv("water_source")} onChange={d("water_source")} options={WATER_SOURCES} span={false} testid="list-water" />
        <Select label="Sewer" value={dv("sewer")} onChange={d("sewer")} options={SEWER} span={false} testid="list-sewer" />
        <Text label="Electricity provider" value={dv("electric_provider")} onChange={d("electric_provider")} testid="list-electric" />
        <Text label="Internet availability" value={dv("internet")} onChange={d("internet")} testid="list-internet" />
        <Select label="Solar panels?" value={dv("solar")} onChange={d("solar")} options={["No", "Yes"]} span={false} testid="list-solar" />
        <Select label="Solar ownership" value={dv("solar_ownership")} onChange={d("solar_ownership")} options={SOLAR_OWNERSHIP} span={false} testid="list-solar-own" />
        <Text label="Flood zone" value={dv("flood_zone")} onChange={d("flood_zone")} testid="list-flood" />
        <Text label="Green / energy certification" value={dv("energy_cert")} onChange={d("energy_cert")} testid="list-energy-cert" />
      </Grid>
      <div className="mt-6 border-t border-slate-100 pt-6">
        <h3 className="mb-4 text-sm font-black uppercase tracking-widest text-slate-500">Disclosures</h3>
        <Grid>
          <Select label="Lead-based paint disclosure" value={dv("lead_paint")} onChange={d("lead_paint")} options={["Not applicable", "Provided", "Required"]} span={false} testid="list-lead" />
          <Text label="Radon information" value={dv("radon")} onChange={d("radon")} testid="list-radon" />
          <TextArea label="Other state-required disclosures" rows={2} value={dv("other_disclosures")} onChange={d("other_disclosures")} testid="list-disclosures" />
        </Grid>
      </div>
    </div>
  );
}

function StepSchools({ form, set, d, dv }) {
  return (
    <div>
      <SectionTitle sub="Present factual, sourced data. Avoid subjective ratings (e.g. 'safe', 'family-friendly').">Schools & neighborhood</SectionTitle>
      <Grid>
        <Text label="Elementary school" value={dv("elementary")} onChange={d("elementary")} testid="list-elementary" />
        <Text label="Middle school" value={dv("middle_school")} onChange={d("middle_school")} testid="list-middle" />
        <Text label="High school" value={dv("high_school")} onChange={d("high_school")} testid="list-high" />
        <Text label="School district" value={form.school_district} onChange={set("school_district")} testid="list-schools-district" />
        <Text label="School-data source / date" value={dv("school_source")} onChange={d("school_source")} testid="list-school-source" />
        <Text label="Transit / nearby transportation" value={dv("transit")} onChange={d("transit")} testid="list-transit" />
        <TextArea label="Neighborhood description (factual)" rows={3} value={dv("neighborhood_desc")} onChange={d("neighborhood_desc")} testid="list-neighborhood" />
      </Grid>
    </div>
  );
}

function StepCategory({ d, dv, isLand, isCommercial, isMulti, form, setForm }) {
  return (
    <div>
      {isLand && (<>
        <SectionTitle sub="Fields for land, farm and ranch listings.">Land & farm details</SectionTitle>
        <Grid>
          <Select label="Land type" req value={dv("land_type")} onChange={d("land_type")} options={["Residential lot", "Acreage", "Farm", "Ranch", "Timberland", "Recreational", "Commercial land", "Waterfront", "Other"]} span={false} testid="list-land-type" />
          <Num label="Total acreage" req value={dv("acreage")} onChange={d("acreage")} testid="list-acreage" />
          <Text label="Lot dimensions" value={dv("lot_dimensions")} onChange={d("lot_dimensions")} placeholder="330 x 660" testid="list-lot-dimensions" />
          <Num label="Number of parcels" value={dv("parcels")} onChange={d("parcels")} testid="list-parcels" />
          <Text label="Zoning" value={dv("zoning")} onChange={d("zoning")} testid="list-zoning" />
          <Text label="Current land use" value={dv("land_use")} onChange={d("land_use")} testid="list-land-use" />
          <Text label="Permitted uses" value={dv("permitted_uses")} onChange={d("permitted_uses")} testid="list-permitted-uses" />
          <Select label="Development status" value={dv("dev_status")} onChange={d("dev_status")} options={["Raw land", "Entitled", "Improved", "Partially developed", "Shovel-ready"]} span={false} testid="list-dev-status" />
          <Select label="Subdivision potential" value={dv("subdivision_potential")} onChange={d("subdivision_potential")} options={["No", "Possible", "Approved"]} span={false} testid="list-subdivision-potential" />
          <Text label="Road frontage" value={dv("road_frontage")} onChange={d("road_frontage")} testid="list-road-frontage" />
          <Select label="Road surface" value={dv("road_surface")} onChange={d("road_surface")} options={["Paved", "Gravel", "Dirt", "None"]} span={false} testid="list-road-surface" />
          <Select label="Legal access" value={dv("legal_access")} onChange={d("legal_access")} options={["Yes", "No", "Easement"]} span={false} testid="list-legal-access" />
          <Text label="Easements" value={dv("easements")} onChange={d("easements")} testid="list-easements" />
          <Text label="Topography" value={dv("topography")} onChange={d("topography")} testid="list-topography" />
          <Text label="Soil type" value={dv("soil_type")} onChange={d("soil_type")} testid="list-soil-type" />
          <Select label="Percolation / soil test" value={dv("perc_test")} onChange={d("perc_test")} options={["Not done", "Passed", "Failed", "Pending"]} span={false} testid="list-perc-test" />
          <Select label="Survey available?" value={dv("survey")} onChange={d("survey")} options={["No", "Yes"]} span={false} testid="list-survey" />
          <Select label="Boundary markers" value={dv("boundary_markers")} onChange={d("boundary_markers")} options={["Yes", "No", "Partial"]} span={false} testid="list-boundary" />
          <Text label="Water / mineral / timber rights" value={dv("rights")} onChange={d("rights")} placeholder="Water incl., mineral excl." testid="list-rights" />
          <Text label="Irrigation" value={dv("irrigation")} onChange={d("irrigation")} testid="list-irrigation" />
          <Text label="Crops / pasture" value={dv("crops")} onChange={d("crops")} testid="list-crops" />
          <Text label="Fencing" value={dv("fencing")} onChange={d("fencing")} testid="list-fencing" />
          <Text label="Utilities available" value={dv("utilities_available")} onChange={d("utilities_available")} placeholder="Power at road, well needed" testid="list-utils-avail" />
          <Text label="Structures on property" value={dv("structures")} onChange={d("structures")} testid="list-structures" />
          <Text label="Agricultural exemptions" value={dv("ag_exemptions")} onChange={d("ag_exemptions")} testid="list-ag-exemptions" />
          <Text label="Conservation easements" value={dv("conservation_easements")} onChange={d("conservation_easements")} testid="list-conservation" />
        </Grid>
      </>)}

      {isMulti && (<>
        <SectionTitle sub="Income and operations for multifamily / residential income listings.">Multifamily / income details</SectionTitle>
        <Grid>
          <Num label="Number of residential units" req value={dv("units")} onChange={d("units")} testid="list-units" />
          <Text label="Unit mix" value={dv("unit_mix")} onChange={d("unit_mix")} placeholder="4× 2bd/1ba, 2× 1bd/1ba" testid="list-unit-mix" />
          <Text label="Beds/baths per unit" value={dv("unit_beds_baths")} onChange={d("unit_beds_baths")} testid="list-unit-beds-baths" />
          <Text label="Rent per unit" value={dv("rent_per_unit")} onChange={d("rent_per_unit")} placeholder="$1,400 avg" testid="list-rent-per-unit" />
          <Text label="Unit square footage" value={dv("unit_sqft")} onChange={d("unit_sqft")} testid="list-unit-sqft" />
          <Num label="Current occupancy rate (%)" value={dv("occupancy_rate")} onChange={d("occupancy_rate")} testid="list-occupancy-rate" />
          <Text label="Lease expiration dates" value={dv("lease_expirations")} onChange={d("lease_expirations")} testid="list-lease-expirations" />
          <Num label="Security deposits held ($)" value={dv("deposits_held")} onChange={d("deposits_held")} testid="list-deposits-held" />
          <Num label="Gross rental income ($/yr)" value={dv("gross_income")} onChange={d("gross_income")} testid="list-gross-income" />
          <Num label="Other income ($/yr)" value={dv("other_income")} onChange={d("other_income")} testid="list-other-income" />
          <Num label="Operating expenses ($/yr)" value={dv("op_expenses")} onChange={d("op_expenses")} testid="list-op-expenses" />
          <Num label="Net operating income ($/yr)" value={dv("noi")} onChange={d("noi")} testid="list-noi-multi" />
          <Num label="Capitalization rate (%)" step="0.1" value={dv("cap_rate")} onChange={d("cap_rate")} testid="list-cap-rate-multi" />
          <Num label="Laundry income ($/yr)" value={dv("laundry_income")} onChange={d("laundry_income")} testid="list-laundry-income" />
          <Num label="Parking income ($/yr)" value={dv("parking_income")} onChange={d("parking_income")} testid="list-parking-income" />
          <Select label="Utility metering" value={dv("metering")} onChange={d("metering")} options={["Separately metered", "Master metered", "Mixed"]} span={false} testid="list-metering" />
        </Grid>
        <DocsSection {...{ form, setForm }} docs={["Rent roll", "Profit & loss statement"]} note="Rent roll and P&L are stored privately and shared only with authorized users — never published publicly." />
      </>)}

      {isCommercial && (<>
        <SectionTitle sub="Fields for office, retail, industrial, warehouse, hospitality and mixed-use.">Commercial details</SectionTitle>
        <Grid>
          <Select label="Commercial subtype" req value={dv("comm_subtype")} onChange={d("comm_subtype")} options={["Office", "Retail", "Industrial", "Warehouse", "Hospitality", "Mixed-use", "Medical", "Flex", "Other"]} span={false} testid="list-comm-subtype" />
          <Select label="Business included in sale?" value={dv("business_included")} onChange={d("business_included")} options={["No", "Yes"]} span={false} testid="list-business-included" />
          <Text label="Building class" value={dv("building_class")} onChange={d("building_class")} placeholder="A / B / C" testid="list-building-class" />
          <Text label="Zoning" value={dv("comm_zoning")} onChange={d("comm_zoning")} testid="list-comm-zoning" />
          <Text label="Permitted use" value={dv("permitted_use")} onChange={d("permitted_use")} testid="list-permitted-use" />
          <Text label="Current use" value={dv("current_use")} onChange={d("current_use")} testid="list-current-use" />
          <Num label="Total building area (sq ft)" value={dv("building_area")} onChange={d("building_area")} testid="list-building-area" />
          <Num label="Available area (sq ft)" value={dv("available_area")} onChange={d("available_area")} testid="list-available-area" />
          <Num label="Min divisible area (sq ft)" value={dv("min_divisible")} onChange={d("min_divisible")} testid="list-min-divisible" />
          <Num label="Max contiguous area (sq ft)" value={dv("max_contiguous")} onChange={d("max_contiguous")} testid="list-max-contiguous" />
          <Num label="Number of buildings" value={dv("num_buildings")} onChange={d("num_buildings")} testid="list-num-buildings" />
          <Num label="Number of floors" value={dv("floors")} onChange={d("floors")} testid="list-floors" />
          <Num label="Ceiling / clear height (ft)" value={dv("ceiling_height")} onChange={d("ceiling_height")} testid="list-ceiling" />
          <Num label="Loading docks" value={dv("loading_docks")} onChange={d("loading_docks")} testid="list-loading-docks" />
          <Num label="Drive-in doors" value={dv("drive_in_doors")} onChange={d("drive_in_doors")} testid="list-drive-in" />
          <Num label="Office area (sq ft)" value={dv("office_area")} onChange={d("office_area")} testid="list-office-area" />
          <Num label="Warehouse area (sq ft)" value={dv("warehouse_area")} onChange={d("warehouse_area")} testid="list-warehouse-area" />
          <Num label="Parking spaces" value={dv("parking_spaces")} onChange={d("parking_spaces")} testid="list-parking" />
          <Text label="Parking ratio" value={dv("parking_ratio")} onChange={d("parking_ratio")} placeholder="4 / 1,000 sq ft" testid="list-parking-ratio" />
          <Num label="Traffic count (vehicles/day)" value={dv("traffic_count")} onChange={d("traffic_count")} testid="list-traffic-count" />
          <Select label="Signage availability" value={dv("signage")} onChange={d("signage")} options={["Building", "Monument", "Pylon", "None"]} span={false} testid="list-signage" />
          <Select label="Elevator" value={dv("elevator")} onChange={d("elevator")} options={["No", "Yes"]} span={false} testid="list-elevator" />
          <Select label="Sprinkler system" value={dv("sprinkler")} onChange={d("sprinkler")} options={["No", "Yes"]} span={false} testid="list-sprinkler" />
          <Select label="Rail access" value={dv("rail_access")} onChange={d("rail_access")} options={["No", "Yes"]} span={false} testid="list-rail" />
          <Text label="Power capacity (phase / voltage)" value={dv("power_capacity")} onChange={d("power_capacity")} placeholder="3-phase, 480V" testid="list-power" />
          <Text label="Tenant improvements" value={dv("tenant_improvements")} onChange={d("tenant_improvements")} testid="list-ti" />
          <Select label="Opportunity-zone status" value={dv("opp_zone")} onChange={d("opp_zone")} options={["No", "Yes"]} span={false} testid="list-opp-zone" />
          <Select label="Enterprise-zone status" value={dv("enterprise_zone")} onChange={d("enterprise_zone")} options={["No", "Yes"]} span={false} testid="list-enterprise-zone" />
          <Text label="Existing tenants" value={dv("existing_tenants")} onChange={d("existing_tenants")} testid="list-existing-tenants" />
          <Text label="Lease terms" value={dv("lease_terms")} onChange={d("lease_terms")} testid="list-lease-terms" />
          <Select label="Sale-leaseback option" value={dv("sale_leaseback")} onChange={d("sale_leaseback")} options={["No", "Yes"]} span={false} testid="list-sale-leaseback" />
        </Grid>
        <DocsSection {...{ form, setForm }} docs={["Offering memorandum", "Investment pro forma", "Rent roll"]} note="Offering memorandum and pro forma are stored privately and shared only with authorized users — never published publicly." />
      </>)}
    </div>
  );
}

function DocsSection({ form, setForm, docs, note }) {
  const [busy, setBusy] = useState(false);
  const list = form.details.documents || [];
  const onFiles = async (files) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    setBusy(true);
    for (const file of arr) {
      try {
        const res = await homesUploadDocument(file);
        setForm((f) => ({ ...f, details: { ...f.details, documents: [...(f.details.documents || []), { name: res.name, path: res.path, url: res.url, visibility: "private" }] } }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Could not upload ${file.name}`);
      }
    }
    setBusy(false);
  };
  const remove = (path) => setForm((f) => ({ ...f, details: { ...f.details, documents: (f.details.documents || []).filter((x) => x.path !== path) } }));
  return (
    <div className="mt-6 border-t border-slate-100 pt-6">
      <h3 className="mb-1 text-sm font-black uppercase tracking-widest text-slate-500">Private documents</h3>
      <p className="mb-3 text-xs text-slate-500">{note} Suggested: {docs.join(", ")}.</p>
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-5 text-sm font-bold text-[#0F172A] transition-colors hover:border-[#2563EB] hover:bg-[#EEF2FF]" data-testid="list-doc-drop">
        {busy ? <Loader2 className="h-5 w-5 animate-spin text-[#2563EB]" /> : <Upload className="h-5 w-5 text-[#2563EB]" />}
        {busy ? "Uploading…" : "Upload PDF, XLS(X), DOC(X) or CSV"}
        <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="list-doc-input" />
      </label>
      {list.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="list-doc-list">
          {list.map((doc, i) => (
            <span key={doc.path} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-[#0F172A]" data-testid={`list-doc-chip-${i}`}>
              <FileText className="h-3.5 w-3.5 text-[#2563EB]" /> {doc.name}
              <Lock className="h-3 w-3 text-slate-400" />
              <button type="button" onClick={() => remove(doc.path)} className="text-[#c8102e]" data-testid={`list-doc-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function StepPhotos({ form, setForm }) {
  const [busy, setBusy] = useState(false);
  const onFiles = async (files) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    setBusy(true);
    for (const file of arr) {
      try {
        const res = await homesUploadPhoto(file);
        const url = res.url?.startsWith("http") ? res.url : `${BACKEND}${res.url}`;
        setForm((f) => ({ ...f, photos: [...f.photos, url] }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Could not upload ${file.name}`);
      }
    }
    setBusy(false);
  };
  const remove = (url) => setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== url) }));
  const makeCover = (url) => setForm((f) => ({ ...f, photos: [url, ...f.photos.filter((p) => p !== url)] }));
  return (
    <div>
      <SectionTitle sub="The first photo is your cover image. JPG, PNG, WEBP or GIF up to 8MB each. Only upload media you own or have permission to publish.">Photos & media</SectionTitle>
      <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-10 text-center transition-colors hover:border-[#2563EB] hover:bg-[#EEF2FF]" data-testid="list-photo-drop">
        {busy ? <Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /> : <Upload className="h-8 w-8 text-[#2563EB]" />}
        <span className="mt-3 text-sm font-bold text-[#0F172A]">{busy ? "Uploading…" : "Click to upload photos"}</span>
        <span className="mt-1 text-xs text-slate-500">You can add up to 50 images</span>
        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="list-photo-input" />
      </label>
      {form.photos.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="list-photo-grid">
          {form.photos.map((url, i) => (
            <div key={url} className="group relative overflow-hidden rounded-xl border border-slate-200">
              <img src={url} alt="" className="h-28 w-full object-cover" />
              {i === 0 && <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-[#2563EB] px-2 py-0.5 text-[10px] font-bold text-white"><Star className="h-3 w-3" /> Cover</span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                {i !== 0 && <button type="button" onClick={() => makeCover(url)} className="rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-bold text-[#0F172A]">Set cover</button>}
                <button type="button" onClick={() => remove(url)} className="ml-auto rounded bg-white/90 p-1 text-[#c8102e]" data-testid={`list-photo-remove-${i}`}><X className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StepOpenHouse({ d, dv }) {
  return (
    <div>
      <SectionTitle sub="Optional — add an open house or set showing preferences.">Open house & showings</SectionTitle>
      <Grid>
        <Text label="Open-house date" type="date" value={dv("oh_date")} onChange={d("oh_date")} testid="list-oh-date" />
        <Text label="Start time" type="time" value={dv("oh_start")} onChange={d("oh_start")} testid="list-oh-start" />
        <Text label="End time" type="time" value={dv("oh_end")} onChange={d("oh_end")} testid="list-oh-end" />
        <Select label="In-person or virtual" value={dv("oh_type")} onChange={d("oh_type")} options={["In-person", "Virtual"]} span={false} testid="list-oh-type" />
        <Select label="Appointment required?" value={dv("appointment")} onChange={d("appointment")} options={["No", "Yes"]} span={false} testid="list-appointment" />
        <Text label="Minimum showing notice" value={dv("min_notice")} onChange={d("min_notice")} placeholder="24 hours" testid="list-min-notice" />
      </Grid>
    </div>
  );
}

function StepContact({ form, set }) {
  return (
    <div>
      <SectionTitle sub="How interested buyers/renters will reach you. Shown on the public listing.">Contact & brokerage</SectionTitle>
      <Grid>
        <Select label="Contact type" req value={form.contact_type} onChange={set("contact_type")} options={CONTACT_TYPES} span={false} testid="list-contact-type" />
        <div />
        <Text label="First name" req value={form.contact_first} onChange={set("contact_first")} testid="list-contact-first" />
        <Text label="Last name" req value={form.contact_last} onChange={set("contact_last")} testid="list-contact-last" />
        <Text label="Phone" req value={form.contact_phone} onChange={set("contact_phone")} testid="list-contact-phone" />
        <Text label="Email" req type="email" value={form.contact_email} onChange={set("contact_email")} testid="list-contact-email" />
        <Text label="Website" value={form.contact_website} onChange={set("contact_website")} testid="list-contact-website" />
        <Text label="Brokerage / company" value={form.brokerage} onChange={set("brokerage")} testid="list-brokerage" />
        <Text label="State license number" value={form.license} onChange={set("license")} testid="list-license" />
      </Grid>
      <p className="mt-4 text-xs text-slate-400">For IDX/MLS listings, the public page must clearly identify the responsible brokerage.</p>
    </div>
  );
}

function StepVerify({ form, set, fhFlags }) {
  const toggle = (k) => (e) => set("confirmations")({ ...form.confirmations, [k]: e.target.checked });
  return (
    <div>
      <SectionTitle sub="Confirm the following before your listing goes live.">Verification & disclosures</SectionTitle>
      {fhFlags.length > 0 && (
        <div className="mb-5 rounded-xl border border-[#c8102e]/30 bg-red-50 p-4 text-sm text-red-800" data-testid="verify-fh-warning">
          <div className="flex items-center gap-2 font-bold text-[#c8102e]"><AlertTriangle className="h-4 w-4" /> Your description still contains flagged language</div>
          <p className="mt-1">Please revisit the Description step and rephrase: {fhFlags.map((f) => `"${f.term}"`).join(", ")}.</p>
        </div>
      )}
      <div className="space-y-2.5" data-testid="verify-confirmations">
        {CONFIRMATIONS.map((c) => (
          <label key={c.k} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 text-sm ${form.confirmations[c.k] ? "border-[#2563EB] bg-[#EEF2FF]" : "border-slate-200 bg-white"}`}>
            <input type="checkbox" checked={!!form.confirmations[c.k]} onChange={toggle(c.k)} className="mt-0.5 h-4 w-4 accent-[#2563EB]" data-testid={`verify-${c.k}`} />
            <span className="font-semibold text-[#0F172A]">{c.label}</span>
          </label>
        ))}
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Text label="Electronic signature (type your full name)" req value={form.signature} onChange={set("signature")} testid="verify-signature" />
        <ReadOnly label="Date" value={new Date().toLocaleDateString()} />
      </div>
      <p className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs leading-relaxed text-slate-500">
        Property information is provided by the listing participant or identified data source and is deemed reliable but is not guaranteed. Buyers and renters should independently verify property details, measurements, schools, taxes, zoning, permits, environmental conditions and availability. Listings are subject to change, withdrawal or prior sale or rental without notice. HomesPulse is an Equal Housing Opportunity platform.
      </p>
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;

/* ---------------- Listing Boost modal (QR + shareable flyer) ---------------- */
function BoostModal({ listing, onClose, onMarketing }) {
  const [kit, setKit] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const flyerRef = useRef(null);

  useEffect(() => { homesMarketingKit(listing.id).then(setKit).catch(() => setKit({ error: true })); }, [listing.id]);

  const shareUrl = kit?.link?.ok && kit.link.short_url
    ? kit.link.short_url
    : `${window.location.origin}/homes/property/${listing.id}`;
  const rent = listing.status === "for_rent";
  const cover = (listing.photos || [])[0];
  const coverSrc = typeof cover === "string" ? cover : (cover?.image_url || cover?.thumb_url);

  const copy = () => { navigator.clipboard?.writeText(shareUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); };

  const downloadFlyer = async () => {
    if (!flyerRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(flyerRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true });
      const a = document.createElement("a");
      a.download = `homespulse-flyer-${listing.id}.png`;
      a.href = dataUrl;
      a.click();
    } catch {
      toast.error("Could not render the flyer. Try again.");
    } finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm" data-testid="boost-modal">
      <div className="my-8 w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF2FF] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#2563EB]"><Rocket className="h-3.5 w-3.5" /> Boost your listing</span>
            <h2 className="mt-2 text-2xl font-black text-[#0F172A]">Your listing is live — now get it seen</h2>
            <p className="text-sm text-slate-500">Share the branded link, print the QR, or download a ready-to-post flyer.</p>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" data-testid="boost-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="mt-5 grid gap-6 md:grid-cols-2">
          {/* Left: link + QR + actions */}
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">Shareable link</label>
            <div className="flex gap-2">
              <input readOnly value={shareUrl} className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-[#0F172A]" data-testid="boost-share-url" />
              <button onClick={copy} className="flex items-center gap-1 rounded-xl bg-[#2563EB] px-3 py-2.5 text-sm font-bold text-white hover:bg-[#1D4ED8]" data-testid="boost-copy">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
            </div>
            {kit && !kit?.link?.ok && (
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">{kit?.link?.message || "A branded splink.pro QR activates on the live production domain; meanwhile the QR below points to your listing."}</p>
            )}
            <div className="mt-4 flex flex-col items-center rounded-xl border border-slate-200 p-4">
              {kit?.link?.ok && kit.link.qr_png ? (
                <img src={kit.link.qr_png} alt="Listing QR" className="h-44 w-44 rounded-lg" data-testid="boost-qr" />
              ) : (
                <div className="rounded-lg bg-white p-2" data-testid="boost-qr"><QRCodeCanvas value={shareUrl} size={176} /></div>
              )}
              <span className="mt-2 text-xs font-semibold text-slate-500">Scan to open the listing</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={downloadFlyer} disabled={downloading} className="flex items-center gap-1.5 rounded-full bg-[#0F172A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0F172A]/90 disabled:opacity-60" data-testid="boost-download-flyer">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download flyer
              </button>
              <button onClick={onMarketing} className="flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-bold text-[#0F172A] hover:bg-slate-50" data-testid="boost-marketing">
                <ExternalLink className="h-4 w-4" /> Marketing Center
              </button>
              <button onClick={onClose} className="rounded-full px-4 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-50" data-testid="boost-view-listing">View listing →</button>
            </div>
          </div>

          {/* Right: flyer preview (also the download target) */}
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">Flyer preview</label>
            <div ref={flyerRef} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="boost-flyer">
              <div className="relative h-44 w-full bg-[#0F172A]">
                {coverSrc ? <img src={coverSrc} crossOrigin="anonymous" alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-white/40"><Home className="h-10 w-10" /></div>}
                <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white ${rent ? "bg-[#0F172A]" : "bg-[#2563EB]"}`}>{rent ? "For Rent" : "For Sale"}</span>
              </div>
              <div className="flex items-start justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="text-2xl font-black text-[#0F172A]">${Number(listing.price || 0).toLocaleString()}{rent ? "/mo" : ""}</div>
                  {listing.headline && <div className="truncate text-sm font-bold text-[#0F172A]">{listing.headline}</div>}
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs font-semibold text-slate-500">
                    {listing.beds > 0 && <span>{listing.beds} bd</span>}
                    {listing.baths > 0 && <span>{listing.baths} ba</span>}
                    {listing.sqft && <span>{Number(listing.sqft).toLocaleString()} sqft</span>}
                  </div>
                  <div className="mt-1 truncate text-xs text-slate-500">{listing.address}</div>
                  <div className="truncate text-xs text-slate-400">{[listing.city_name, listing.state_name].filter(Boolean).join(", ")}</div>
                </div>
                <div className="shrink-0 rounded-lg bg-white p-1"><QRCodeCanvas value={shareUrl} size={72} /></div>
              </div>
              <div className="flex items-center justify-between bg-[#2563EB] px-4 py-2 text-[11px] font-bold text-white">
                <span>HomesPulse<span className="opacity-70">.pro</span></span>
                <span className="opacity-90">Scan to view this home</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
